/**
 * m-3d-motion.js
 * Sitewide 3D motion with VARIED effects (no single repeated animation):
 *   1) Product images get one of 4 different auto-motion styles,
 *      cycled so neighbouring cards don't move identically.
 *   2) Every scroll-revealed block gets one of 5 different 3D
 *      entrance styles, cycled per block.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouchOrCoarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  var skipDecorativeMotion = prefersReducedMotion || isTouchOrCoarse;

  var FLOAT_VARIANTS = ['m-anim-1', 'm-anim-2', 'm-anim-3', 'm-anim-4', 'm-anim-5', 'm-anim-6'];
  var REVEAL_VARIANTS = ['m-reveal-rise', 'm-reveal-left', 'm-reveal-right', 'm-reveal-zoom', 'm-reveal-flip'];

  /* ---------- 1) Assign varied auto-motion to product images ----------
     Scoped per Shopify section, with a rotating offset, so that a
     collection grid in Section A and a slider in Section B don't end
     up using the exact same sequence of effects. */
  function assignFloatVariants() {
    var sections = document.querySelectorAll(
      '.shopify-section:not(.shopify-section-annoucement):not(.shopify-section-scrolling-promotion)'
    );
    var sectionOffset = 0;

    sections.forEach(function (section) {
      var floaters = section.querySelectorAll(
        '.m-product-card__media, .m-product-media--item'
      );
      if (!floaters.length) return;

      floaters.forEach(function (el, i) {
        if (el.dataset.mAnimAssigned) return;
        if (el.closest('m-product-details-tabs')) return;
        el.dataset.mAnimAssigned = 'true';
        var variantIndex = (i + sectionOffset) % FLOAT_VARIANTS.length;
        el.classList.add(FLOAT_VARIANTS[variantIndex]);
        el.classList.add('m-video-glare');
        el.style.setProperty('--float-delay', ((i % 6) * 0.35).toFixed(2));
      });

      /* Shift the starting variant for the next section so it opens
         on a different effect than this one did. */
      sectionOffset += 2;
    });
  }

  /* ---------- 2) Assign varied scroll-reveal styles per block ---------- */
  function markRevealTargets() {
    var counter = 0;
    var sections = document.querySelectorAll(
      '.shopify-section:not(.shopify-section-annoucement):not(.shopify-section-scrolling-promotion):not(.shopify-section-header):not(.shopify-section-footer)'
    );
    sections.forEach(function (section) {
      if (section.querySelector('m-header')) return;
      var blocks = section.querySelectorAll(':scope > * > *');
      var targets = blocks.length ? blocks : section.children;
      Array.prototype.forEach.call(targets, function (el) {
        if (el.dataset.mRevealAssigned) return;
        if (el.closest('m-product-details-tabs')) return;
        el.dataset.mRevealAssigned = 'true';
        el.classList.add('m-reveal-3d');
        el.classList.add(REVEAL_VARIANTS[counter % REVEAL_VARIANTS.length]);
        counter++;
      });
    });
  }

  function initRevealObserver() {
    if (prefersReducedMotion) return null;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('m-is-inview');
          } else {
            /* leaving the viewport: reset so the animation replays
               next time the element scrolls back into view */
            entry.target.classList.remove('m-is-inview');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    document.querySelectorAll('.m-reveal-3d').forEach(function (el) {
      observer.observe(el);
    });

    return observer;
  }

  function init() {
    if (!skipDecorativeMotion) {
      assignFloatVariants();
      markRevealTargets();
    }
    var observer = skipDecorativeMotion ? null : initRevealObserver();

    var debounceId;
    var mutationObserver = new MutationObserver(function () {
      clearTimeout(debounceId);
      debounceId = setTimeout(function () {
        if (!skipDecorativeMotion) {
          assignFloatVariants();
          markRevealTargets();
          if (observer) {
            document.querySelectorAll('.m-reveal-3d').forEach(function (el) {
              observer.observe(el);
            });
          }
        }
        /* Shared signal: other scripts (product-card-spin.js,
           m-3d-tilt-extra.js) listen for this instead of each
           running their own full-body MutationObserver. */
        document.dispatchEvent(new CustomEvent('m:dom-mutated'));
      }, 200);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
/**
 * m-3d-tilt-extra.js
 * Adds a soft auto-moving colorful glow spot to image cards & product
 * card media — driven by ONE shared requestAnimationFrame loop for the
 * whole page (not one loop per element), so it doesn't pile up extra
 * concurrent animation loops as more cards get added (ajax cart,
 * infinite scroll, quick view, etc).
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  // Mobile/touch devices never trigger this hover-style glow effect, yet the
  // requestAnimationFrame loop below was still running forever for every
  // product/image card on the page — pure battery/CPU drain with zero visual
  // benefit on a phone. Bail out completely on coarse-pointer (touch) devices.
  var isCoarsePointer = window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  if (isCoarsePointer) return;

  var GLOW_SELECTORS = '.m-image-card__inner, .m-product-card__media';
  var SPEED = 0.00045;

  var glowItems = [];
  var rafId = null;

  function tick(ts) {
    for (var i = 0; i < glowItems.length; i++) {
      var item = glowItems[i];
      var t = ts * SPEED + item.phase;
      var gx = 50 + Math.sin(t) * 35;
      var gy = 50 + Math.cos(t * 0.8) * 35;
      item.glow.style.setProperty('--gx', gx.toFixed(1) + '%');
      item.glow.style.setProperty('--gy', gy.toFixed(1) + '%');
    }
    rafId = glowItems.length ? requestAnimationFrame(tick) : null;
  }

  function ensureLoopRunning() {
    if (rafId === null && glowItems.length) {
      rafId = requestAnimationFrame(tick);
    }
  }

  function attachAutoGlow(el, index) {
    if (el.__mGlowInit) return;
    el.__mGlowInit = true;

    var glow = document.createElement('div');
    glow.className = 'm-tilt-glow';
    glow.style.cssText =
      'position:absolute;inset:0;pointer-events:none;border-radius:inherit;' +
      'z-index:2;mix-blend-mode:soft-light;' +
      'background:radial-gradient(circle at var(--gx,50%) var(--gy,50%),' +
      'rgba(255,255,255,0.45),rgba(124,58,237,0) 55%);';
    if (getComputedStyle(el).position === 'static') {
      el.style.position = 'relative';
    }
    el.appendChild(glow);

    /* Each card gets its own phase offset so the glow doesn't move
       identically across every card on the page. */
    var phase = (index % 8) * 0.9;
    glowItems.push({ glow: glow, phase: phase });
  }

  function scan() {
    var els = document.querySelectorAll(GLOW_SELECTORS);
    els.forEach(function (el, i) {
      attachAutoGlow(el, i);
    });
    ensureLoopRunning();
  }

  function init() {
    scan();
    // Shared DOM-change signal (from m-3d-motion.js) instead of
    // running another full-body MutationObserver here.
    document.addEventListener('m:dom-mutated', scan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
/**
 * product-card-spin.js
 * On hover, cycles a product card's extra photos in quick succession
 * to fake a simple "turntable" spin of the product itself, using only
 * images the merchant already uploaded (3+ images required per product).
 */
(function () {
  if (window.__mCardSpinInit) return;
  window.__mCardSpinInit = true;

  var FRAME_MS = 220;
  var isCoarsePointer =
    window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  function initCard(media) {
    if (media.__spinInit || isCoarsePointer) return;
    var raw = media.getAttribute('data-rotate-images');
    if (!raw) return;
    var urls = raw.split('|').filter(Boolean);
    if (urls.length < 2) return;

    var frame = media.querySelector('.m-product-card__rotate-frame');
    var img = frame ? frame.querySelector('img') : null;
    if (!frame || !img) return;

    media.__spinInit = true;

    var timer = null;
    var idx = 0;

    function preload() {
      urls.forEach(function (src) {
        var i = new Image();
        i.src = src;
      });
    }
    preload();

    function tick() {
      img.src = urls[idx % urls.length];
      idx++;
    }

    media.addEventListener('mouseenter', function () {
      idx = 0;
      media.classList.add('is-spinning');
      tick();
      timer = window.setInterval(tick, FRAME_MS);
    });

    media.addEventListener('mouseleave', function () {
      media.classList.remove('is-spinning');
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    });
  }

  function setup(root) {
    (root || document)
      .querySelectorAll('.m-product-card__media[data-rotate-images]')
      .forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setup();
    });
  } else {
    setup();
  }

  document.addEventListener('shopify:section:load', function (e) {
    setup(e.target);
  });

  // Cards can appear after AJAX filtering / infinite scroll etc.
  // Uses the shared DOM-change signal (m-3d-motion.js) instead of
  // running its own full-body MutationObserver.
  document.addEventListener('m:dom-mutated', function () {
    setup();
  });
})();

/**
 * PERF FIX: pause the always-on decorative loops (image-card tilt/glow,
 * gold-shimmer product titles, spinning social icons, footer/nav shimmer)
 * while they are scrolled out of view. On a homepage with many sections
 * and product cards, most of these were animating forever even off-screen
 * — pure CPU/GPU cost with zero visible effect. This adds/removes a single
 * class that sets animation-play-state:paused (see m-effects-bundle.css),
 * so the look is 100% unchanged for anything the visitor can actually see.
 */
(function () {
  'use strict';
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var SELECTORS = [
    '.m-image-card__inner',
    '.m-featured-collection .m-product-card__name',
    '.social-media-links--item a',
    '.social-media-links a',
    '.m-button'
  ].join(',');

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      entry.target.classList.toggle('m-anim-paused', !entry.isIntersecting);
    });
  }, { rootMargin: '200px 0px 200px 0px', threshold: 0 });

  function observeAll(root) {
    (root || document).querySelectorAll(SELECTORS).forEach(function (el) {
      if (el.__mAnimObserved) return;
      el.__mAnimObserved = true;
      io.observe(el);
    });
  }

  function init() { observeAll(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Reuse the shared DOM-change signal dispatched above instead of a
  // second full-body MutationObserver.
  document.addEventListener('m:dom-mutated', function () { observeAll(); });
  document.addEventListener('shopify:section:load', function (e) { observeAll(e.target); });
})();
